import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CartSidebar from "@/components/feature/CartSidebar";
import ProductCard from "@/components/base/ProductCard";
import { useProducts } from "@/hooks/useProducts";

const styleFilters = ["All", "Classic Cat Eye", "Galaxy", "Aurora", "Velvet"];
const priceRanges = [
  { label: "Under $20", min: 0, max: 20 },
  { label: "$20 – $25", min: 20, max: 25 },
];

export default function NailsPage() {
  const [activeStyle, setActiveStyle] = useState("All");
  const [activePriceIdx, setActivePriceIdx] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("featured");

  const { products: nailsProducts, loading } = useProducts({ category: "nails" });

  const filtered = nailsProducts.filter((p) => {
    const styleMatch =
      activeStyle === "All" ||
      (activeStyle === "Classic Cat Eye" && p.fabricLabel === "Cat Eye Glow" && !p.name.toLowerCase().includes("velvet") && !p.name.toLowerCase().includes("galaxy")) ||
      (activeStyle === "Galaxy" && p.name.toLowerCase().includes("galaxy")) ||
      (activeStyle === "Aurora" && p.fabricLabel === "Aurora Chrome") ||
      (activeStyle === "Velvet" && p.name.toLowerCase().includes("velvet"));

    const priceMatch =
      activePriceIdx === null ||
      (p.price >= priceRanges[activePriceIdx].min && p.price <= priceRanges[activePriceIdx].max);

    return styleMatch && priceMatch;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "price-asc") return a.price - b.price;
    if (sortBy === "price-desc") return b.price - a.price;
    if (sortBy === "rating") return b.rating - a.rating;
    return (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0);
  });

  return (
    <div className="min-h-screen bg-moonlight" style={{ fontFamily: "'Jost', sans-serif" }}>
      <Navbar />
      <CartSidebar />

      {/* Hero Banner */}
      <section className="relative h-[340px] md:h-[460px] flex items-end overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=artistic%20close%20up%20of%20multiple%20female%20hands%20with%20stunning%20cat%20eye%20magnetic%20gel%20press%20on%20nails%20in%20various%20colors%20including%20moonlight%20white%20silver%20aurora%20rose%20gold%20and%20midnight%20purple%2C%20hands%20elegantly%20arranged%20holding%20a%20champagne%20glass%20and%20pearl%20clutch%2C%20dark%20luxury%20atmospheric%20background%20with%20soft%20bokeh%20and%20candlelight%20reflections%2C%20high%20fashion%20nail%20beauty%20campaign%20photography%2C%20glamorous%20feminine%20glow%20aesthetic&width=1920&height=920&seq=iif_nails_hero_001&orientation=landscape"
          alt="i-nails — luminous press-on nails"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/40 via-midnight/20 to-midnight/70" />
        <div className="relative z-10 px-6 lg:px-16 pb-12 md:pb-16 w-full">
          <p
            className="text-[10px] tracking-widest uppercase text-moonlight/60 mb-2"
            style={{ letterSpacing: "0.3em" }}
          >
            i-if collection
          </p>
          <h1
            className="text-4xl md:text-6xl font-light italic text-moonlight leading-none mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            i-nails
          </h1>
          <p className="text-sm text-moonlight/70 max-w-sm">
            Cat eye. Aurora. Galaxy. Velvet. Luminous press-on nails for every mood and moment.
          </p>
        </div>
      </section>

      {/* Shipping Bar */}
      <div className="bg-midnight text-moonlight/80 py-3 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 text-[10px] tracking-widest uppercase">
          <span className="flex items-center gap-2">
            <i className="ri-truck-line" /> Free shipping on orders over $80
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-refresh-line" /> 30-day returns
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-shield-check-line" /> Reusable up to 3 wears
          </span>
        </div>
      </div>

      {/* Style Quick-Links */}
      <div className="border-b border-pearl/30 bg-moonlight">
        <div className="max-w-6xl mx-auto px-6 lg:px-0 flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
          {styleFilters.map((style) => (
            <button
              key={style}
              onClick={() => setActiveStyle(style)}
              className={`whitespace-nowrap px-5 py-2 rounded-full text-xs tracking-wider border transition-all cursor-pointer ${
                activeStyle === style
                  ? "bg-midnight text-moonlight border-midnight"
                  : "border-pearl/50 text-midnight/60 hover:border-midnight hover:text-midnight"
              }`}
            >
              {style}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 lg:px-0 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Sidebar Filter */}
          <aside className="lg:w-52 shrink-0">
            {/* Glow Type */}
            <div className="mb-8">
              <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">
                Glow Type
              </h3>
              <div className="flex flex-col gap-2">
                {styleFilters.map((style) => (
                  <button
                    key={style}
                    onClick={() => setActiveStyle(style)}
                    className={`text-left text-xs tracking-wide py-1 transition-colors cursor-pointer ${
                      activeStyle === style
                        ? "text-midnight font-medium"
                        : "text-midnight/50 hover:text-midnight"
                    }`}
                  >
                    {activeStyle === style && (
                      <span className="mr-2 text-moon-gold-dark">—</span>
                    )}
                    {style}
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="mb-8">
              <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">
                Price
              </h3>
              <div className="flex flex-col gap-2">
                {priceRanges.map((range, idx) => (
                  <button
                    key={range.label}
                    onClick={() =>
                      setActivePriceIdx(activePriceIdx === idx ? null : idx)
                    }
                    className={`text-left text-xs tracking-wide py-1 transition-colors cursor-pointer ${
                      activePriceIdx === idx
                        ? "text-midnight font-medium"
                        : "text-midnight/50 hover:text-midnight"
                    }`}
                  >
                    {activePriceIdx === idx && (
                      <span className="mr-2 text-moon-gold-dark">—</span>
                    )}
                    {range.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {(activeStyle !== "All" || activePriceIdx !== null) && (
              <button
                onClick={() => {
                  setActiveStyle("All");
                  setActivePriceIdx(null);
                }}
                className="text-[10px] tracking-widest uppercase text-midnight/40 hover:text-midnight transition-colors cursor-pointer underline underline-offset-2"
              >
                Clear All
              </button>
            )}
          </aside>

          {/* Products */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-8">
              <p className="text-xs text-midnight/50">
                {sorted.length} {sorted.length === 1 ? "style" : "styles"}
              </p>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs text-midnight border border-pearl/50 bg-transparent px-3 py-1.5 rounded-md cursor-pointer focus:outline-none focus:border-midnight"
              >
                <option value="featured">Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="rating">Top Rated</option>
              </select>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-7">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-pearl/30 aspect-[3/4] mb-3" />
                    <div className="bg-pearl/30 h-4 w-3/4 mb-2" />
                    <div className="bg-pearl/30 h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : sorted.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-7">
                {sorted.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-24">
                <p className="text-sm text-midnight/40 mb-3">No styles match your filters.</p>
                <button
                  onClick={() => {
                    setActiveStyle("All");
                    setActivePriceIdx(null);
                  }}
                  className="text-xs tracking-widest uppercase text-midnight/60 hover:text-midnight transition-colors cursor-pointer underline underline-offset-2"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Complete the Look Banner */}
      <section
        className="py-16 px-6 lg:px-16"
        style={{
          background: "linear-gradient(135deg, #1A2A32 0%, #0F1C24 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p
              className="text-[10px] tracking-widest uppercase text-moonlight/40 mb-3"
              style={{ letterSpacing: "0.3em" }}
            >
              Complete the Look
            </p>
            <h2
              className="text-3xl md:text-4xl font-light italic text-moonlight mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Complete your glow from head to toe.
            </h2>
            <p className="text-sm text-moonlight/50 max-w-md mx-auto">
              Pair your luminous nails with our dresses, shoes, and bags — every detail in the same stunning glow.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                label: "i-clothes",
                sub: "Satin Dresses & Tops",
                href: "/collections/clothes",
                image: "https://readdy.ai/api/search-image?query=luxury%20silver%20satin%20midi%20slip%20dress%20hanging%20elegantly%20in%20dark%20studio%20with%20moonlight%20illumination%2C%20fashion%20campaign%20photography%2C%20luminous%20fabric%20shine&width=600&height=400&seq=iif_nail_clt_001&orientation=landscape",
              },
              {
                label: "i-bag",
                sub: "Satin & Holographic Bags",
                href: "/collections/bags",
                image: "https://readdy.ai/api/search-image?query=iridescent%20holographic%20evening%20clutch%20bag%20and%20satin%20shoulder%20bag%20on%20dark%20velvet%20surface%2C%20luxury%20accessories%20photography%2C%20moonlight%20reflection&width=600&height=400&seq=iif_nail_bag_001&orientation=landscape",
              },
              {
                label: "i-shoes",
                sub: "Patent & Metallic Heels",
                href: "/collections/shoes",
                image: "https://readdy.ai/api/search-image?query=pearl%20patent%20pointed%20toe%20stilettos%20and%20gold%20metallic%20strappy%20heels%20arranged%20on%20dark%20marble%2C%20luxury%20shoe%20photography%2C%20moonlight%20glow&width=600&height=400&seq=iif_nail_sho_001&orientation=landscape",
              },
            ].map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="group relative overflow-hidden block cursor-pointer"
                style={{ aspectRatio: "3/2" }}
              >
                <img
                  src={item.image}
                  alt={item.label}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-midnight/40 group-hover:bg-midnight/30 transition-colors" />
                <div className="absolute bottom-0 left-0 right-0 px-5 py-5">
                  <p
                    className="text-xl font-light italic text-moonlight"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {item.label}
                  </p>
                  <p className="text-[9px] tracking-widest uppercase text-moonlight/60 mt-0.5">
                    {item.sub}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Glow Edit Teaser */}
      <section className="py-14 px-6 bg-moonlight-warm">
        <div className="max-w-2xl mx-auto text-center">
          <p
            className="text-[10px] tracking-widest uppercase text-midnight/40 mb-3"
            style={{ letterSpacing: "0.3em" }}
          >
            From the Edit
          </p>
          <h3
            className="text-2xl md:text-3xl font-light italic text-midnight mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            How to Style Cat Eye Nails for Your Next Party
          </h3>
          <p className="text-sm text-midnight/50 mb-6 leading-relaxed">
            The right nails don't just complete your look — they start the conversation. Discover how to match Moonlight Cat Eye Nails with our Shimmer Midi Dress and Iridescent Chain Bag for a night you won't forget.
          </p>
          <Link
            to="/pages/the-glow-edit"
            className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-midnight border-b border-midnight pb-0.5 hover:text-moon-gold-dark hover:border-moon-gold-dark transition-colors cursor-pointer"
          >
            Read the Story <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
